import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2, Smartphone, Globe, ArrowRight } from 'lucide-react';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(4);

  const isNative = typeof window !== 'undefined' && Boolean(window.Capacitor?.isNativePlatform?.());

  const openNativeApp = () => {
    if (isNative) {
      navigate('/');
      return;
    }
    // Intentar abrir app nativa con esquema registrado
    window.location.href = 'portalgastos://open';
  };

  const continueInBrowser = () => {
    navigate('/');
  };

  const verifyEmail = useCallback(async (token) => {
    try {
      const response = await axios.get(`${API}/auth/verify-email/${token}`);
      setStatus('success');
      setMessage(response.data.message || 'Correo verificado correctamente');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Error al verificar el email');
    }
  }, []);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Token de verificación no encontrado');
      return;
    }

    verifyEmail(token);
  }, [searchParams, verifyEmail]);

  // Manejo de redirección automática al verificar exitosamente
  useEffect(() => {
    if (status !== 'success') return;

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    // Si es navegador móvil externo, intentar abrir app tras 1.5s
    let appTimer;
    if (isMobile && !isNative) {
      appTimer = setTimeout(() => {
        openNativeApp();
      }, 1500);
    }

    // Contador regresivo y redirección al inicio de sesión tras 4s
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (appTimer) clearTimeout(appTimer);
      clearInterval(interval);
    };
  }, [status, navigate, isNative]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="p-8 max-w-md w-full bg-white/90 backdrop-blur-sm border-slate-200 shadow-xl text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Verificando correo...</h2>
            <p className="text-slate-600">Estamos validando tu cuenta, espera un momento.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-black text-slate-900 mb-2">¡Correo Verificado!</h2>
            <p className="text-slate-600 mb-6 text-sm">
              Tu cuenta ha sido activada con éxito. Ya puedes comenzar a registrar tus finanzas.
            </p>

            <div className="space-y-3">
              {isNative ? (
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.99] text-white font-bold py-3.5 px-4 rounded-xl shadow-md transition-all text-sm"
                >
                  <ArrowRight className="w-4 h-4" />
                  Iniciar Sesión
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={openNativeApp}
                    className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99] text-white font-bold py-3 px-4 rounded-xl shadow-md transition-all text-sm"
                  >
                    <Smartphone className="w-4 h-4" />
                    Abrir en la App
                  </button>

                  <button
                    type="button"
                    onClick={continueInBrowser}
                    className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 active:scale-[0.99] text-slate-700 font-bold py-3 px-4 rounded-xl transition-all text-sm"
                  >
                    <Globe className="w-4 h-4" />
                    Continuar en Navegador / PWA
                  </button>
                </>
              )}
            </div>

            <p className="text-xs text-slate-400 mt-5">
              Redirigiendo automáticamente en <span className="font-bold text-slate-600">{countdown}s</span>...
            </p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-rose-600" />
            </div>
            <h2 className="text-2xl font-black text-slate-900 mb-2">Verificación</h2>
            <p className="text-slate-600 mb-6 text-sm">
              {message === 'Invalid verification token'
                ? 'Este enlace ya fue utilizado o tu cuenta ya está verificada.'
                : message}
            </p>

            <div className="space-y-3">
              <button
                type="button"
                onClick={openNativeApp}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-4 rounded-xl text-sm transition-all"
              >
                <Smartphone className="w-4 h-4" />
                Abrir en la App
              </button>

              <button
                type="button"
                onClick={() => navigate('/')}
                className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2.5 px-4 rounded-xl text-sm transition-all"
              >
                <ArrowRight className="w-4 h-4" />
                Ir a Iniciar Sesión
              </button>
            </div>
          </>
        )}
        
        <div className="mt-8 pt-4 border-t border-slate-200">
          <a 
            href="https://vjrodriguez.dev" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            Hecho por VJRDEV
          </a>
        </div>
      </Card>
    </div>
  );
};

export default VerifyEmail;